import axios from "axios";
import { store, Actions } from "../store";
import { toast } from "react-toastify";
import { Helper } from ".";

export const APP_BASE_URL: string = process.env.APP_BASE_URL;
export const API_KEY: string = process.env.API_KEY;

const http = axios.create({
  baseURL: `${APP_BASE_URL}/`,
  validateStatus: (status) => status >= 200 && status < 300,
  xsrfCookieName: "XSRF-TOKEN",
  xsrfHeaderName: "X-CSRF-Token",
  withCredentials: false,
});
http.interceptors.request.use((config) => {
  delete config.headers["Cache-Control"];
  return config;
});
// --- Frontend Caching Layer ---
interface CacheEntry {
  data: any;
  expiry: number;
}

const cacheMap = new Map<string, CacheEntry>();

// --- Token Refresh State ---
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};
// -----------------------------

const getCacheKey = (config: any) => {
  const url = config.url || "";
  const params = config.params ? JSON.stringify(config.params) : "";
  // Cached responses must never be shared across different logged-in
  // identities — without this, a GET hit for one user/store can be served
  // back verbatim to a different user/store that happens to request the
  // same URL+params (e.g. two stores both loading their "Order Placed" list).
  const identity = config.authorization ? Helper.getUser()?.id ?? "anon" : "public";
  return `${config.method || "get"}:${identity}:${url}:${params}`;
};

const clearCacheByPattern = (pattern: string) => {
  for (const key of cacheMap.keys()) {
    if (key.includes(pattern)) {
      cacheMap.delete(key);
    }
  }
};
// -----------------------------

const storeProcess = async (config: any, data: any) => {
  const actionType: "set" | "append" | "update" | "remove" | "reset" =
    config.action;
  if (actionType !== "reset") {
    store.dispatch(Actions[actionType](config.key, data));
  } else {
    store.dispatch(Actions["reset"](config.key));
  }
};

const loadingProcess = async (config: any, loading = false) => {
  if (!!config?.store) {
    const actionType: "set" | "update" | "remove" | "reset" =
      config?.store?.action;
    if (actionType === "set") {
      const loadingData: any = {
        loading: loading,
        loadingState: true,
      };
      store.dispatch(Actions[actionType](config?.store?.key, loadingData));
    }
  }
};

const request = async (configuration: any) => {
  const { authorization, config, ...restConfiguration } = configuration;
  const defaultHeader: any = {
    // 'Content-Type': 'application/json',
    // Accept: "application/json",
    "Api-Key": process.env.API_KEY,
    // "Cache-Control": "no-cache",
  };

  if (!!authorization) {
    const user = Helper.getUser();
    if (!user?.token) {
      console.warn("No token found in localStorage for user:", user);
    } else {
      defaultHeader.Authorization = `Bearer ${user?.token}`;
    }
  }

  const method = (restConfiguration?.method || "get").toLowerCase();
  const cacheKey = getCacheKey({ ...restConfiguration, method, authorization });

  // 1. Check cache for GET requests
  if (
    method === "get" &&
    configuration.cacheTTL &&
    !configuration.forceRefresh
  ) {
    const cached = cacheMap.get(cacheKey);
    if (cached && cached.expiry > Date.now()) {
      // console.log(`[Cache Hit] ${cacheKey}`);
      const data = cached.data;
      if (!!config?.store) {
        await storeProcess(config.store, data);
      }
      return data;
    }
  }

  // 2. Invalidate cache for mutations
  if (["post", "put", "patch", "delete"].includes(method)) {
    const baseUrlPath = restConfiguration?.url?.split("/")[0] || "";
    if (baseUrlPath) {
      // console.log(`[Cache Invalidate] Pattern: ${baseUrlPath}`);
      clearCacheByPattern(baseUrlPath);
    }
  }

  await loadingProcess(config, true);
  return await http({
    ...restConfiguration,
    url: restConfiguration?.url?.toString?.() || restConfiguration?.url,
    headers: {
      ...restConfiguration.headers,
      ...defaultHeader,
    },
  })
    .then(async (resp) => {
      if (!!resp?.data?.errors) {
        const firstErrorMessage =
          resp?.data?.errors?.[0]?.message || "Request failed";
        throw new Error(firstErrorMessage);
      }
      const data = resp?.data?.data;

      // 3. Store in cache if TTL is provided
      if (method === "get" && configuration.cacheTTL) {
        cacheMap.set(cacheKey, {
          data,
          expiry: Date.now() + configuration.cacheTTL,
        });
      }

      if (!!config?.store) {
        await storeProcess(config.store, data);
      }
      if (!!configuration?.config?.successMsg) {
        // toast.dismiss();
        toast.success(configuration?.config?.successMsg, {
          toastId: "global-success",
        });
      }

      return data;
    })
    .catch(async (err) => {
      const originalRequest = configuration;
      const status = err?.response?.status;
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0]?.message ||
        err?.message ||
        "Something went wrong";

      // Handle 401 Unauthorized
      if (
        (status === 401 || message === "Unauthorized") &&
        !originalRequest._retry
      ) {
        const user = Helper.getUser();
        const role = window.location.pathname.startsWith("/store")
          ? "store"
          : "admin";

        // Only attempt refresh for admin role since it's the only one implemented on backend
        if (role === "admin" && user?.refreshToken) {
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                return request({ ...originalRequest, _retry: true });
              })
              .catch((err) => {
                throw err;
              });
          }

          originalRequest._retry = true;
          isRefreshing = true;

          try {
            const resp = await axios.post(
              `${APP_BASE_URL}/admins/refresh`,
              {
                refreshToken: user.refreshToken,
              },
              {
                headers: {
                  "Api-Key": process.env.API_KEY,
                },
              },
            );

            const newData = resp.data.data;
            if (newData) {
              console.info(`[Auth] Refresh successful, new token acquired.`);
              Helper.saveUser(newData);
              isRefreshing = false;
              processQueue(null, newData.accessToken || newData.token);
              return request(originalRequest);
            }
          } catch (refreshErr) {
            isRefreshing = false;
            processQueue(refreshErr, null);
            Helper.logout(store.dispatch);
            throw refreshErr;
          }
        } else {
          Helper.logout(store.dispatch);
        }
      }

      if (!!config?.showErr) {
        toast.error(message, { toastId: "global-error" });
      }

      if (!(status === 401 || message === "Unauthorized")) {
        await loadingProcess(config, false);
      }

      throw new Error(message);
    });
};

export default request;
